import { apiClient } from './apiClient';

export interface Permission {
  id: string;
  code: string;
  name: string;
  module: string;
  description?: string;
}

export interface RoleWithPermissions {
  id: string;
  code: string;
  name: string;
  description?: string;
  isSystem: boolean;
  userCount: number;
  permissionIds: string[];
}

export const rolesService = {
  async listPermissions(): Promise<Permission[]> {
    const { data } = await apiClient.get<Permission[]>('/admin/roles/permissions');
    return data;
  },

  async getMatrix(): Promise<RoleWithPermissions[]> {
    const { data } = await apiClient.get<RoleWithPermissions[]>('/admin/roles');
    return data;
  },

  /** Simple list dùng cho dropdown — gọi được bởi mọi user auth, không cần SYS_ADMIN */
  async listSimple(): Promise<Array<{ id: string; code: string; name: string; description?: string }>> {
    const { data } = await apiClient.get('/roles');
    return data;
  },

  async updatePermissions(roleId: string, permissionIds: string[]): Promise<void> {
    await apiClient.put(`/admin/roles/${roleId}/permissions`, { permissionIds });
  },

  async createRole(payload: { code: string; name: string; description?: string }): Promise<RoleWithPermissions> {
    const { data } = await apiClient.post<RoleWithPermissions>('/admin/roles', payload);
    return data;
  },

  async updateRole(id: string, payload: { name?: string; description?: string }): Promise<RoleWithPermissions> {
    const { data } = await apiClient.patch<RoleWithPermissions>(`/admin/roles/${id}`, payload);
    return data;
  },

  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/admin/roles/${id}`);
  },
};

export interface OrgNode {
  id: string;
  code: string;
  name: string;
  parentId?: string | null;
  managerId?: string | null;
  managerName?: string | null;
  managerPosition?: string | null;
  userCount: number;
  children: OrgNode[];
  /** V1.0.32: ID nhóm khoa phòng (null = chưa phân nhóm) */
  groupId?: string | null;
  /** V1.0.32: Tên nhóm */
  groupName?: string | null;
  /** V1.0.32: Màu nhóm */
  groupColor?: string | null;
  /** V1.0.47: Trạng thái active. FE filter out khi chọn dept cho task/project/document. */
  isActive?: boolean;
}

/** V1.0.32: Nhóm khoa phòng */
export interface DepartmentGroup {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  color: string;
  sortOrder: number;
  isActive: boolean;
  departmentCount: number;
  createdAt: string;
}

export interface CreateDepartmentGroupRequest {
  code: string;
  name: string;
  description?: string;
  color?: string;
  sortOrder?: number;
}

export interface UpdateDepartmentGroupRequest {
  code?: string;
  name?: string;
  description?: string;
  color?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export const orgService = {
  /**
   * Lấy cây org cho user thường — dùng khi cần chọn khoa phòng
   */
  async getTree(): Promise<OrgNode[]> {
    const { data } = await apiClient.get<OrgNode[]>('/org/tree');
    return data;
  },

  /**
   * Lấy cây org cho admin
   */
  async getAdminTree(): Promise<OrgNode[]> {
    const { data } = await apiClient.get<OrgNode[]>('/admin/org/tree');
    return data;
  },

  // ============================================================
  // V1.0.32: Department Groups
  // ============================================================

  /** List nhóm khoa phòng (public) — dùng cho FE để filter/group */
  async listGroups(): Promise<DepartmentGroup[]> {
    const { data } = await apiClient.get<DepartmentGroup[]>('/org/groups');
    return data;
  },

  /** List nhóm khoa phòng (admin) */
  async listGroupsAdmin(): Promise<DepartmentGroup[]> {
    const { data } = await apiClient.get<DepartmentGroup[]>('/admin/org/groups');
    return data;
  },

  async createGroup(payload: CreateDepartmentGroupRequest): Promise<DepartmentGroup> {
    const { data } = await apiClient.post<DepartmentGroup>('/admin/org/groups', payload);
    return data;
  },

  async updateGroup(id: string, payload: UpdateDepartmentGroupRequest): Promise<DepartmentGroup> {
    const { data } = await apiClient.put<DepartmentGroup>(`/admin/org/groups/${id}`, payload);
    return data;
  },

  async deleteGroup(id: string): Promise<void> {
    await apiClient.delete(`/admin/org/groups/${id}`);
  },

  /** Gán khoa vào nhóm (groupId = null để bỏ ra) */
  async assignDeptToGroup(deptId: string, groupId: string | null): Promise<void> {
    await apiClient.put(`/admin/org/departments/${deptId}/group`, { groupId });
  },
};
